import { ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DSM3NavItem } from '@eredes/dsm3';
import { DSM34Dialog } from '@eredes/dsm34';
import * as CryptoJS from 'crypto-js';
import { BehaviorSubject, Subject } from 'rxjs';

import { UserModel } from '../../_core/models/user.model';
import { ProfileService } from '../../_core/services/profile.service';
import { UserService } from '../../_core/services/user.service';
import { EdpValidators } from '../../_shared/edp-validators/edp-validators';
import { GenericModalComponent } from '../../_shared/generic-modal/generic-modal.component';

import { UserProfileModel } from './models/user-profile.model';
import { UserRequestPermissionModel } from './models/user-request-permission.model';
import { UserCreateEditService } from './user-create-edit.service';

import { CRYPTO_KEY } from 'app/_core/models/global-variables.model';

@Component({
  selector: 'app-user-create-edit',
  templateUrl: './user-create-edit.component.html',
  standalone: false,
})
export class UserCreateEditComponent implements OnInit {
  breadcrumbsItems$: Subject<DSM3NavItem[]> = new BehaviorSubject<DSM3NavItem[]>([]);
  public isEdit: boolean;
  public curUser: UserModel;
  public curUserIsRetailer: boolean;
  public curUserisBpo: boolean;
  public userForm: UntypedFormGroup;
  public allPermissions: UserRequestPermissionModel[];
  public profiles: UserProfileModel[];
  public assignableVisible: boolean;
  private userId: string;
  public retailersList: any[];
  public isProfileOrd = false;
  public isProfileRet = false;
  public dialog = inject(DSM34Dialog);

  permissions: {
    permKey: string;
    permId: string;
    permCode: string;
    permName: string;
  }[] = [];

  /**
   * Form Period
   */
  get formControls(): Record<string, AbstractControl> {
    return this.userForm.controls;
  }

  constructor(
    protected route: ActivatedRoute,
    protected router: Router,
    protected userService: UserService,
    protected profileService: ProfileService,
    protected userCreateEditService: UserCreateEditService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.initializePermissions();

    this.route.params.subscribe((data) => {
      if (data.id) {
        const bytes = CryptoJS.AES.decrypt(data.id.toString(), CRYPTO_KEY);
        this.userId = bytes.toString(CryptoJS.enc.Utf8);
        this.isEdit = !!data.id;
      }
    });

    this.setBreadcrumb();

    this.curUser = null;
    this.profiles = [];
    this.isProfileOrd = this.userService.isProfileOrd();
    this.isProfileRet = this.userService.isProfileRetailer();
    this.curUserIsRetailer = this.userService.isProfileRetailer();
    this.curUserisBpo = this.userService.isProfileOrd();

    this.initForm();

    if (!this.isEdit) {
      if (this.isProfileRet) {
        this.permInformationCPEInstance('create');
      }

      this.userCreateEditService.getInitialData().subscribe(
        (data: any) => {
          this.retailersList = data.retailers;
          this.profiles = data.profiles;
          this.allPermissions = data.permissions;
          this.addPermissionsToForm();
          this.cdr.detectChanges();
        },
        (err: any) => {
          console.error(err);
          this.retailersList = [];
          this.profiles = [];
          this.allPermissions = [];
          this.cdr.detectChanges();
        }
      );
    } else {
      this.userCreateEditService.getUser(this.userId).subscribe(
        (userData: any) => {
          this.curUser = userData;
          this.curUserIsRetailer = this.curUser.profileName.startsWith('RET');
          this.curUserisBpo = this.curUser.profileName.startsWith('BPO');

          if (this.curUserIsRetailer) {
            this.permInformationCPEInstance('create');
          }

          this.userCreateEditService.getInitialData().subscribe(
            (data: any) => {
              this.retailersList = data.retailers;
              this.profiles = data.profiles;
              this.allPermissions = data.permissions;
              this.addPermissionsToForm();
              this.fillForm(this.curUser);
              this.cdr.detectChanges();
            },
            (err: any) => {
              console.error(err);
              this.retailersList = [];
              this.profiles = [];
              this.allPermissions = [];
              this.cdr.detectChanges();
            }
          );

          this.cdr.detectChanges();
        },
        (err: any) => {
          console.error(err);
          this.cdr.detectChanges();
        }
      );
    }

    this.watchProfile();
  }

  private initializePermissions(): void {
    this.permissions = [
      {
        permKey: 'perm_invoicing_write',
        permId: 'pt.edp.permission.106',
        permCode: 'invoicing_write',
        permName: 'Faturação Escrita',
      },
      {
        permKey: 'perm_scheduling_read',
        permId: 'pt.edp.permission.107',
        permCode: 'scheduling_read',
        permName: 'Agendamentos Leitura',
      },
      {
        permKey: 'perm_scheduling_write',
        permId: 'pt.edp.permission.9',
        permCode: 'scheduling_write',
        permName: 'Agendamentos Escrita',
      },
      {
        permKey: 'perm_user_read',
        permId: 'pt.edp.permission.109',
        permCode: 'user_read',
        permName: 'Utilizadores Leitura',
      },
      {
        permKey: 'perm_user_write',
        permId: 'pt.edp.permission.108',
        permCode: 'user_write',
        permName: 'Utilizadores Escrita',
      },
      {
        permKey: 'perm_contract_write',
        permId: 'pt.edp.permission.110',
        permCode: 'contract_write',
        permName: 'Contratos Escrita',
      },
      {
        permKey: 'perm_energy_write',
        permId: 'pt.edp.permission.111',
        permCode: 'energy_write',
        permName: 'Energia Escrita',
      },
      {
        permKey: 'perm_messaging_write',
        permId: 'pt.edp.permission.124',
        permCode: 'messaging_write',
        permName: 'Messaging',
      },
      {
        permKey: 'perm_messaging_productor_write',
        permId: 'pt.edp.permission.131',
        permCode: 'messaging_productor_write',
        permName: 'Messaging Produtor',
      },
    ];
  }

  private setBreadcrumb(): void {
    const breadcrumbsItems: DSM3NavItem[] = [
      {
        label: 'Utilizadores',
        href: '/user',
      },
      {
        label: this.isEdit ? 'Edição de Utilizador' : 'Criação de Utilizador',
        href: '/user/user-create',
      },
    ];
    this.breadcrumbsItems$.next(breadcrumbsItems);
  }

  protected initForm() {
    this.userForm = new UntypedFormGroup({});

    this.userForm.addControl(
      'curUserName',
      new UntypedFormControl(null, [EdpValidators.required, EdpValidators.maxLength(60)])
    );
    if (this.isEdit) {
      this.userForm.addControl(
        'curUserUsername',
        new UntypedFormControl({ value: null, disabled: true }, [EdpValidators.required, EdpValidators.maxLength(20)])
      );
      this.userForm.addControl(
        'curUserEmail',
        new UntypedFormControl({ value: null, disabled: true }, [EdpValidators.required, EdpValidators.email])
      );
    } else {
      this.userForm.addControl(
        'curUserUsername',
        new UntypedFormControl(
          null,
          [EdpValidators.required, EdpValidators.maxLength(20), EdpValidators.username],
          [this.userService.existingUserAccountValidator()]
        )
      );
      this.userForm.addControl(
        'curUserEmail',
        new UntypedFormControl(
          null,
          [EdpValidators.required, EdpValidators.email],
          [this.userService.existingEmailValidator()]
        )
      );
    }

    this.userForm.addControl(
      'curUserIsRetailer',
      new UntypedFormControl(this.curUserIsRetailer ? this.userService.getRetailerUniqueCode() : null)
    );
    this.userForm.addControl(
      'curUserRetailerUniqueCode',
      new UntypedFormControl(this.curUserIsRetailer ? this.userService.getRetailerUniqueCode() : null)
    );
    this.userForm.addControl('curUserPhone', new UntypedFormControl(null, EdpValidators.phoneNumber));
    this.userForm.addControl('curUserPosition', new UntypedFormControl(null, EdpValidators.maxLength(40)));
    this.userForm.addControl(
      'curUserProfile',
      new UntypedFormControl(
        this.isProfileOrd ? null : this.userService.getProfileUniqueCode(),
        this.isProfileOrd ? EdpValidators.required : null
      )
    );
    this.userForm.addControl('curUserAssignable', new UntypedFormControl(0, EdpValidators.required));
    this.permissions.forEach((permission) => {
      this.userForm.addControl(permission.permKey, new UntypedFormControl(0, EdpValidators.required));
    });
  }

  protected fillForm(data: any) {
    // Remove these 2 line below once profile migration is done
    // (that is, every user with a profileName ending with "_DEFAULT"
    const profilePrefix = data.profileName.substr(0, 3);
    const { profileUniqueCode } = this.profiles.find((profile) => profile.profileName.startsWith(profilePrefix));

    this.userForm.get('curUserName').setValue(data.userName);
    this.userForm.get('curUserUsername').setValue(data.userAccount);
    this.userForm.get('curUserEmail').setValue(data.userEmail);
    this.userForm.get('curUserIsRetailer').setValue(!!data.retailerUniqueCode);
    this.userForm.get('curUserRetailerUniqueCode').setValue(data.retailerUniqueCode);
    this.userForm.get('curUserPhone').setValue(data.userPhone);
    this.userForm.get('curUserPosition').setValue(data.userPosition);
    this.userForm.get('curUserProfile').setValue(profileUniqueCode);
    this.userForm.get('curUserAssignable').setValue(parseInt(data.userAssignable, 10));
    this.userForm.addControl('curUserUniqueCode', new UntypedFormControl(data.userUniqueCode));
  }

  protected addPermissionsToForm() {
    const uniqueAreas = Array.from(new Set(this.allPermissions.map((permission) => permission.areaUniqueCode)));
    const userPermissions = this.allPermissions.filter(
      (permission) => this.curUser && this.curUser.userPermissions.includes(permission.permissionName)
    );
    let hasReadAccess = false;
    let hasWriteAccess = false;

    uniqueAreas.forEach((areaUniqueCode) => {
      if (this.isEdit) {
        const selectedAreas = userPermissions.filter((permission) => permission.areaUniqueCode === areaUniqueCode);
        hasWriteAccess = selectedAreas.some((permission) => !!permission.permissionName.match('_write_'));
        hasReadAccess = selectedAreas.some((permission) => !!permission.permissionName.match('_read_'));
      }
      this.userForm.addControl('perm_' + areaUniqueCode + '_write', new UntypedFormControl(hasWriteAccess));
      this.userForm.addControl(
        'perm_' + areaUniqueCode + '_read',
        new UntypedFormControl(hasReadAccess && !hasWriteAccess)
      );
    });

    let hasPermission: number;
    this.permissions.forEach((permission) => {
      hasPermission =
        this.isEdit && this.curUser.userPermissions && this.curUser.userPermissions.indexOf(permission.permCode) >= 0
          ? 1
          : 0;

      this.userForm.get(permission.permKey).setValue(hasPermission);
    });
  }

  get curUserUsername() {
    return this.userForm.get('curUserUsername');
  }

  get curUserEmail() {
    return this.userForm.get('curUserEmail');
  }

  get curUserPhone() {
    return this.userForm.get('curUserPhone');
  }

  protected userCreatedSuccessModal() {
    return this.dialog.open(GenericModalComponent, {
      data: {
        modalTitle: 'Sucesso',
        modalText: this.isEdit ? 'Utilizador editado com sucesso.' : 'Utilizador criado com sucesso.',
        success: true,
        btnConfirmText: 'Ok',
      },
    });
  }

  protected userCreatedErrorModal() {
    return this.dialog.open(GenericModalComponent, {
      data: {
        modalTitle: 'Erro',
        modalText: 'Ocorreu um problema, por favor volte a tentar.',
        error: true,
        btnConfirmText: 'Ok',
      },
    });
  }

  protected getSelectedRequestPermissions(): {
    areaUniqueCode: string;
    accessMode: string;
  }[] {
    return Object.keys(this.userForm.value)
      .filter((input) => !!input.match('perm_pt.edp.request.operation.') && this.userForm.value[input])
      .map((permission) => {
        const [areaUniqueCode, accessMode] = permission.replace('perm_', '').split('_');

        return { areaUniqueCode, accessMode };
      });
  }

  protected getSelectedMenuPermissions(): string[] {
    return this.permissions
      .filter((permission) => this.userForm.get(permission.permKey) && this.userForm.get(permission.permKey).value)
      .map((permission) => permission.permId);
  }

  // Show data after form submit and set submitted to true
  public onSubmit() {
    const formData = this.userForm.value;
    formData['requestPermissions'] = this.getSelectedRequestPermissions();
    formData['permissions'] = this.getSelectedMenuPermissions();

    this.userCreateEditService.save$(formData).subscribe(
      () => {
        const dialogRef = this.userCreatedSuccessModal();
        dialogRef.afterClosed().subscribe(() => this.router.navigate(['user']));
      },
      (err: any) => {
        console.error(err);
        this.userCreatedErrorModal();
      }
    );
  }

  public resetForm() {
    this.userForm.reset();
  }

  public cancel() {
    this.resetForm();

    this.router.navigate(['user']);
  }

  /**
   * Toggle permission_read and permission_write value
   * @param event
   */
  switchPermission(event: Event) {
    const { checked, name } = event.target as HTMLInputElement;

    if (!checked) {
      return;
    }

    const readPermKey = name.replace('_write', '_read');
    const writePermKey = name.replace('_read', '_write');

    if (name === writePermKey && this.userForm.controls.hasOwnProperty(readPermKey)) {
      this.userForm.controls[readPermKey].setValue(!this.userForm.controls[writePermKey].value);
    }
    if (name === readPermKey && this.userForm.controls.hasOwnProperty(writePermKey)) {
      this.userForm.controls[writePermKey].setValue(!this.userForm.controls[readPermKey].value);
    }
  }

  watchProfile(): void {
    this.userForm.get('curUserProfile').valueChanges.subscribe((value) => {
      const { profileName } = this.profiles.find((profile) => profile.profileUniqueCode === value);

      if (profileName.startsWith('RET')) {
        this.curUserIsRetailer = true;
        this.curUserisBpo = false;
        this.formControls.curUserRetailerUniqueCode.setValidators(EdpValidators.required);

        this.permInformationCPEInstance('create');
      } else {
        this.permInformationCPEInstance('remove');
        this.curUserIsRetailer = false;
        this.curUserisBpo = profileName.startsWith('BPO');
        this.formControls.curUserRetailerUniqueCode.reset();
        this.formControls.curUserRetailerUniqueCode.clearValidators();
      }
      this.formControls.curUserRetailerUniqueCode.updateValueAndValidity();
      this.cdr.detectChanges();
    });
  }

  hasSelectedPermission() {
    return Object.keys(this.userForm.controls)
      .filter((control: string) => control.startsWith('perm_'))
      .some((permissionControl: string) => {
        return /(true|1)/i.test(this.formControls[permissionControl].value);
      });
  }

  /**
   * Function that adds or removes the CPE information permission
   * depending on the action that is sent via parameter
   * @param  action - "create" | "remove"
   */
  permInformationCPEInstance(action: 'create' | 'remove') {
    const perm = {
      permKey: 'perm_information_cpe',
      permId: 'pt.edp.permission.126',
      permCode: 'information_cpe',
      permName: 'Informação CPE Escrita',
    };

    const hasPermInformationCPE = this.permissions.some((permission) => permission.permId === 'pt.edp.permission.126');

    if (action === 'create') {
      if (!hasPermInformationCPE) {
        this.permissions.push(perm);
        this.userForm.addControl(perm.permKey, new UntypedFormControl(0, EdpValidators.required));
      }

      return;
    }

    const index = this.permissions.findIndex((permission) => permission.permId === 'pt.edp.permission.126');

    if (index !== -1) {
      this.permissions.splice(index, 1);
      this.userForm.removeControl(perm.permKey);
    }
  }
}
